// Reload-once recovery for failed dynamic imports. All browser access is injected so
// the logic runs under plain Node in tests.

export const CHUNK_RELOAD_KEY = 'tt_chunk_reload';
export const CHUNK_RELOAD_WINDOW_MS = 5 * 60 * 1000;

export interface ChunkReloadDeps {
   // Lazy: reading window.sessionStorage can itself throw (blocked site data).
   getStorage: () => Pick<Storage, 'getItem' | 'setItem'>;
   fetchFn: (url: string, init?: RequestInit) => Promise<{ ok: boolean }>;
   reload: () => void;
   // Lazy: the SPA may have navigated since the guard was installed.
   getHref: () => string;
   // Lazy: false once the app has mounted. After that Nuxt's router handles chunk errors,
   // and a late preload failure must not reload a page the user is already using.
   isActive?: () => boolean;
   now?: () => number;
}

// Returns the handler for `vite:preloadError`. It reloads at most once per window and
// only when the page itself can be fetched, so an offline device or a server that is
// down never enters a reload loop. The timestamp is written before the fetch so a
// burst of failing chunks starts one attempt, not several.
export const createChunkReloadGuard = (deps: ChunkReloadDeps) => {
   const now = deps.now ?? Date.now;
   let started = false;
   return (): void => {
      if (started) return;
      if (deps.isActive && !deps.isActive()) return;
      try {
         const storage = deps.getStorage();
         const last = Number(storage.getItem(CHUNK_RELOAD_KEY) || 0);
         if (now() - last < CHUNK_RELOAD_WINDOW_MS) return;
         storage.setItem(CHUNK_RELOAD_KEY, String(now()));
      } catch {
         return;
      }
      started = true;
      deps
         .fetchFn(deps.getHref(), { cache: 'reload' })
         .then((res) => {
            if (res.ok) deps.reload();
         })
         .catch(() => {});
   };
};
